import { LANGUAGE_CODES } from "./entries/languages";

const isProductionEnvironment = process.env["NODE_ENV"] === "production";
const NON_PROD_LOCALES = ["xx", "zz"] as const;
const VALID_LOCALES = isProductionEnvironment
    ? LANGUAGE_CODES
    : ([...LANGUAGE_CODES, ...NON_PROD_LOCALES] as const);

type ExtractTypeFromArray<T extends Iterable<unknown>> =
    T extends Iterable<infer E> ? E : never;

export type SupportedLocales = ExtractTypeFromArray<typeof VALID_LOCALES>;

export const isValidLocale = (locale: string): locale is SupportedLocales =>
    (VALID_LOCALES as readonly string[]).includes(locale);

/**
 * Route definitions for Trello Portal
 * This is a subset of wac-routes.ts focused only on trello routes
 */
export type TrelloRouteWithVariant = {
    locale: SupportedLocales;
    featureFlags: Record<string, boolean>;
    product?: string;
    path: string;
    variant: string | undefined;
    search: string;
};

export const getFeatureFlagValues = (params: string[]) => {
    // set defaults for feature flags
    const featureFlags = {
        enableBundlesAfterSignup: false,
        enableCutSignUpStepsInHalf: false,
        provisionSecondPartyApps: false
    };
    if (params[0]) {
        params[0] = params[0].replace(/^\?(.+)/, "$1");
    }
    params.forEach((param) => {
        const [key, value] = param.split("=");
        switch (key) {
            case "provisionSecondPartyApps":
                if (value === "enableProvisionSecondPartyApps") {
                    featureFlags.provisionSecondPartyApps = true;
                }
                break;
        }
    });
    return featureFlags;
};

const normalizeHomePath = (path: string) =>
    path === "" || path === "index.html" ? "home" : path;

/**
 * Parse the browser location into a structured route object
 *
 * @param location - Browser Location object
 * @returns Parsed route with locale, path, variant, and additional metadata
 *
 * @example
 * const route = parseRoute(window.location);
 * // Returns: { path: "home", locale: "en", variant: undefined, ... }
 */

export const parseRoute = (
    location: Location
): TrelloRouteWithVariant & {
    locale: SupportedLocales;
    featureFlags: object;
} => {
    // Remove leading and trailing slashes (the server can supply two leading slashes).
    const noSlashPrefixPath = location.pathname.replace(/^\/+|\/+$/g, "");
    // check if the route has a locale prefix
    const potentialLocale = noSlashPrefixPath.split("/")[0] || "";
    const localeIsValid = isValidLocale(potentialLocale);

    // if there is a locale prefix, set the locale variable and remove the prefix from the path
    // otherwise, set the locale to the default (en) and leave the path as is
    const locale = localeIsValid ? potentialLocale : "en";
    const cleanedPath = normalizeHomePath(
        localeIsValid
            ? noSlashPrefixPath.slice(locale.length + 1)
            : noSlashPrefixPath
    );

    const params = (location.search || "").split("&");

    // Extract variant from query params (e.g., ?cmsVariant=some-variant)
    const cmsVariantValue = params
        .filter((param) => param.includes("cmsVariant"))[0]
        ?.split("=")[1] as TrelloRouteWithVariant["variant"];

    const featureFlags = getFeatureFlagValues(params);

    return {
        path: cleanedPath,
        variant: cmsVariantValue,
        locale,
        search: location.search,
        featureFlags
    } as TrelloRouteWithVariant & {
        locale: SupportedLocales;
        featureFlags: object;
    };
};

// UI message bundles are shared with WAC; Content API locales remain unchanged.
export const getTrelloLanguagePackLocale = (locale: string): string => {
    switch (locale) {
        case "pt-BR":
            return "br";
        case "zh-Hans":
            return "zh";
        case "de":
        case "es":
        case "fr":
        case "it":
        case "ja":
        case "nl":
        case "pl":
        case "ru":
        case "xx":
        case "zz":
            return locale;
        default:
            return "en";
    }
};
